import React from 'react';
import { Link } from 'react-router-dom';
import MarketingPageShell from './MarketingPageShell';
import { TEAM_JACK_PROFILE_IMAGE, TEAM_JEROME_PROFILE_IMAGE } from '../config/marketing';
import './MarketingPages.css';

function TeamMember({ name, imageSrc, children }) {
  return (
    <div className="about-team-member">
      <div className="about-team-avatar-wrap">
        <img className="about-team-avatar" src={imageSrc} alt={`Portrait of ${name}`} />
      </div>
      <div className="about-team-text">
        <h3 className="about-team-name">{name}</h3>
        <div className="about-team-bio">{children}</div>
      </div>
    </div>
  );
}

function AboutUsPage() {
  return (
    <MarketingPageShell>
      <section className="marketing-hero">
        <h1>About ShokIQ</h1>
        <p className="lede">
          We built ShokIQ because great suspension telemetry should not require a motorsport budget or an engineering department.
        </p>
      </section>

      <div className="marketing-inner">
        <Link to="/" className="marketing-back">
          ← Back to home
        </Link>

        <div className="marketing-card">
          <h2>Why this product exists</h2>
          <p>
            For years, the choice felt binary: consumer gadgets that oversimplify, or pro-grade systems that are expensive, finicky,
            and slow to turn into real setup changes. We kept seeing the same frustration on the trail—riders who care about suspension
            but do not have time to become signal-processing experts.
          </p>
          <p>
            ShokIQ is our answer: <strong>affordable hardware</strong> that records how your fork and shock actually move, paired
            with a <strong>portal that prioritizes clarity</strong>. You get high-level insight after every ride (balance, stroke usage,
            events) and the depth to go further when you want—velocity bands, stroke depth percentiles, before/after comparisons, and
            video sync so context stays tied to the numbers.
          </p>
          <p>
            It is <strong>not a toy</strong> (the metrics are meaningful) and <strong>not a full laboratory stack</strong> (you should
            still ride, not only graph). It is a <strong>useful tool for mountain bikers and mechanics</strong> who want evidence-backed
            tuning without losing the weekend to spreadsheets.
          </p>
        </div>

        <div className="marketing-card">
          <h2>Meet the team</h2>
          <div className="about-team-grid">
            <TeamMember name="Jack Harle" imageSrc={TEAM_JACK_PROFILE_IMAGE}>
              <p>
                Jack is an extraordinary engineer and a suspension guru—where bikes, parts, and kinematics meet on the trail. He is
                the person behind <strong>Vessel Bikes</strong> when it comes to <strong>bicycle design</strong>: how their bikes are
                conceived and how they ride. He is also highly skilled in <strong>mechanical engineering</strong> and{' '}
                <strong>electronics</strong> in his broader engineering work. Off the clock, he is a genuinely fast mountain biker, racing downhill in the pro division.
              </p>
            </TeamMember>
            <TeamMember name="Jerome Lacote" imageSrc={TEAM_JEROME_PROFILE_IMAGE}>
              <p>
                Jerome is a veteran designer with a sharp focus on UI/UX. He has shipped work for Fortune 500 companies before turning
                his attention to sports tech. He works with BrakeAce, the leading brake-sensor company, has worked with Strava, and
                leads the user-facing direction of major product efforts. A downhill racer at heart, he is passionate about being
                competitive—always chasing a faster lap, refining his own riding, and helping others sharpen their edge too. He stays
                active in the race scene.
              </p>
            </TeamMember>
          </div>
        </div>

        <div className="marketing-card">
          <h2>Ready to go deeper?</h2>
          <p>
            Read our <Link to="/philosophy">tuning philosophy</Link> for how we interpret Basic Stats and related metrics, or{' '}
            <Link to="/pre-order">pre-order the system</Link> to get hardware in the queue.
          </p>
        </div>
      </div>
    </MarketingPageShell>
  );
}

export default AboutUsPage;
